// Wire and storage shapes shared by the server (run directly by Node's type
// stripping) and the client (bundled by Vite). Type-only apart from tiny
// helpers: nothing here may need a TypeScript transform to run.

/** recognize: see or hear the front, recall the meaning. produce: see the meaning, say or recall the front. */
export type CardKind = 'recognize' | 'produce';
export const CARD_KINDS: readonly CardKind[] = ['recognize', 'produce'];
export type Rating = 1 | 2 | 3 | 4;

export interface TtsClip {
  voice: string;
  hash: string;
}

export interface Deck {
  id: string;
  name: string;
  /** Key into LANGUAGES: picks the voices, the field labels and how readings render. */
  language: string;
  kinds: Record<CardKind, boolean>;
  newPerDay: number;
  /** Off: kept in your library, but never studied and given no audio. Missing means on. */
  active?: boolean;
  position: number;
  createdAt: number;
  /** Last-writer-wins clock for every field. */
  updatedAt: number;
  deleted: boolean;
}

/** One thing to learn. Each note yields a recognize card and a produce card. */
export interface Note {
  id: string;
  deckId: string;
  /** What you're learning: a phrase, a word, a question. On audio decks the voices read it. */
  text: string;
  /** Pronunciation aid (jyutping, pinyin, romaji) or any extra line. */
  reading: string;
  /** Its meaning, in your own language: the answer to a recognize card. */
  meaning: string;
  notes: string;
  tags: string[];
  /** New-card order within a deck. Seeded notes count up from 1; notes you add are 0, so they come first. */
  position: number;
  /** Audio you recorded for this note (sha256 hashes). Preferred over TTS. */
  recordings: string[];
  createdAt: number;
  /** Last-writer-wins clock for every field above. */
  updatedAt: number;
  deleted: boolean;
  /** Generated by the server from `text` in the deck's voices. Clients never write it. */
  tts: TtsClip[];
}

/**
 * One answer to one card. The log is append-only and card schedules are
 * derived by replaying it, so two devices that reviewed offline merge by
 * union. `deleted` is the one mutable bit, for undo after a sync.
 */
export interface Review {
  id: string;
  /** `${noteId}:${kind}` */
  cardId: string;
  rating: Rating;
  reviewedAt: number;
  durationMs: number;
  deleted: boolean;
}

export interface SyncRequest {
  /** Highest change sequence already pulled. */
  since: number;
  decks: Deck[];
  notes: Note[];
  reviews: Review[];
}

export interface SyncResponse {
  cursor: number;
  more: boolean;
  decks: Deck[];
  notes: Note[];
  reviews: Review[];
}

export function cardId(noteId: string, kind: CardKind): string {
  return `${noteId}:${kind}`;
}

export function splitCardId(id: string): { noteId: string; kind: CardKind } {
  const i = id.lastIndexOf(':');
  return { noteId: id.slice(0, i), kind: id.slice(i + 1) as CardKind };
}
